import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes, SelectHTMLAttributes } from 'react';
import { forwardRef, useId } from 'react';

interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  htmlFor?: string;
}

export function Field({ label, hint, error, children, htmlFor }: FieldProps) {
  return (
    <div className="field">
      {label && <label htmlFor={htmlFor}>{label}</label>}
      {children}
      {error
        ? <span className="err" role="alert">{error}</span>
        : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  leading?: ReactNode;
  trailing?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leading, trailing, className = '', id, ...rest }, ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const inputCls = [
    'input',
    error ? 'err-state' : '',
    leading ? 'with-lead' : '',
    trailing ? 'with-trail' : '',
    className,
  ].filter(Boolean).join(' ');

  const inner = (
    <input ref={ref} id={inputId} className={inputCls} {...rest} />
  );

  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId}>
      {leading || trailing ? (
        <div className="input-wrap">
          {leading && <span className="lead">{leading}</span>}
          {inner}
          {trailing && <span className="trail">{trailing}</span>}
        </div>
      ) : inner}
    </Field>
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className = '', id, ...rest }, ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId}>
      <textarea ref={ref} id={inputId} className={`textarea ${error ? 'err-state' : ''} ${className}`.trim()} {...rest} />
    </Field>
  );
});

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, className = '', id, children, ...rest }, ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId}>
      <div className="select-wrap">
        <select ref={ref} id={inputId} className={`select ${className}`.trim()} {...rest}>
          {children}
        </select>
      </div>
    </Field>
  );
});
